import { textoUsuarios } from "@/lib/utils/planes";

/**
 * Datos de la landing pública de Qualidot (planes, FAQ, constantes de marca).
 * Fuente única para la UI y para los datos estructurados JSON-LD (SEO).
 */

// Landing y plataforma pueden vivir en hosts distintos (mismo despliegue):
// NEXT_PUBLIC_SITE_URL = www..., NEXT_PUBLIC_APP_URL = app...
const SITE_URL = (process.env.NEXT_PUBLIC_SITE_URL || "https://qualidot.ai").replace(/\/+$/, "");
const APP_URL = (process.env.NEXT_PUBLIC_APP_URL || SITE_URL).replace(/\/+$/, "");

export const SITE = {
  name: "Qualidot",
  url: SITE_URL,
  tagline: "Evaluación con IA de llamadas, clases y video contra tus propias rúbricas",
  description:
    "Qualidot evalúa audio, video, imágenes y documentos contra rúbricas versionadas: transcribe, analiza con IA y entrega scoring con evidencia. Para call centers, universidades, QA y sector público.",
  loginPath: `${APP_URL}/auth/login`,
  salesEmail: "ventas@qualidot.ai",
} as const;

export interface Plan {
  id: string;
  name: string;
  audience: string;
  priceUSD: number | null; // null = "Contactar con ventas"
  priceLabel?: string;
  /** Horas de video estimadas ("hasta"). */
  hours: string;
  /** Las mismas horas de video como número, para la calculadora; `null` = a la medida. */
  videoHours?: number | null;
  /** Horas de audio estimadas ("hasta"); el audio cuesta ≈ la mitad. */
  audioHours?: string;
  /** Emoji del plan, para reconocerlo de un vistazo entre cinco tarjetas. */
  emoji?: string;
  /** Tamaño de equipo recomendado, ya redactado: «1 a 5 usuarios». */
  usuarios?: string;
  files: string;
  featured?: boolean;
  cta: { label: string; href: string };
  note?: string;
}

/**
 * Forma mínima del plan tal como sale de la base (`listPublicPlans`). Se
 * declara estructural para que este módulo, que importan componentes cliente,
 * no dependa del servicio de servidor.
 */
export interface PlanDb {
  id: number; name: string; audience: string | null; priceCents: number | null;
  videoHours: number | null; audioHours: number | null; fileQuota: number | null;
  emoji?: string | null; minUsers?: number | null; maxUsers?: number | null;
  featured: boolean; isContact: boolean;
}

const nfMX = new Intl.NumberFormat("es-MX");

/**
 * Plan de la base → tarjeta de la landing. Única traducción para `/` y
 * `/test_pagina`, que antes la duplicaban. El factor de ganancia NO viaja:
 * `listPublicPlans` no lo devuelve y aquí no hay forma de colarlo.
 */
export function planDesdeDb(p: PlanDb): Plan {
  return {
    id: String(p.id),
    name: p.name,
    audience: p.audience ?? "",
    priceUSD: p.isContact ? null : p.priceCents != null ? Math.round(p.priceCents / 100) : null,
    priceLabel: p.isContact ? "Contactar" : undefined,
    hours: p.videoHours != null ? `${nfMX.format(p.videoHours)} h video` : "Volumen a la medida",
    videoHours: p.videoHours,
    audioHours: p.audioHours != null ? `${nfMX.format(p.audioHours)} h audio` : undefined,
    emoji: p.emoji ?? undefined,
    usuarios: textoUsuarios(p) ?? undefined,
    files: p.fileQuota != null ? `${nfMX.format(p.fileQuota)} archivos` : "Lotes de archivos",
    featured: p.featured,
    note: p.isContact ? "B2B | B2G" : undefined,
    cta: p.isContact
      ? { label: "Contactar con ventas", href: `mailto:${SITE.salesEmail}` }
      : { label: "Comenzar", href: SITE.loginPath },
  };
}

/** Fallback estático (junta del 2026-09-02). La fuente real es la tabla `subscription`. */
export const PLANS: Plan[] = [
  {
    id: "basico",
    emoji: "🌱",
    usuarios: "1 a 5 usuarios",
    name: "Básico",
    audience: "Para pilotos, profesores y validaciones iniciales.",
    priceUSD: 79,
    hours: "50 h video",
    videoHours: 50,
    audioHours: "100 h audio",
    files: "3,000 archivos",
    cta: { label: "Comenzar", href: SITE.loginPath },
  },
  {
    id: "estandar",
    emoji: "🚀",
    usuarios: "10 a 30 usuarios",
    name: "Estándar",
    audience: "Para áreas QA y equipos con evaluación recurrente.",
    priceUSD: 399,
    hours: "250 h video",
    videoHours: 250,
    audioHours: "500 h audio",
    files: "15,000 archivos",
    cta: { label: "Comenzar", href: SITE.loginPath },
  },
  {
    id: "pro",
    emoji: "⚡",
    usuarios: "50 a 100 usuarios",
    name: "Pro",
    audience: "Para operaciones con volumen alto y varios equipos de QA.",
    priceUSD: 999,
    hours: "750 h video",
    videoHours: 750,
    audioHours: "1,500 h audio",
    files: "45,000 archivos",
    cta: { label: "Comenzar", href: SITE.loginPath },
  },
  {
    id: "enterprise",
    emoji: "🏢",
    usuarios: "150 a 200 usuarios",
    name: "Enterprise",
    audience: "Para Call Centers, Universidades y Sector Público.",
    priceUSD: 1599,
    hours: "1,250 h video",
    videoHours: 1250,
    audioHours: "2,500 h audio",
    files: "95,000 archivos",
    featured: true,
    cta: { label: "Comenzar", href: SITE.loginPath },
  },
  {
    id: "personalizado",
    emoji: "🛠️",
    name: "Personalizado",
    audience: "Para operación institucional, integraciones y contratos B2B | B2G.",
    priceUSD: null,
    priceLabel: "Contactar",
    hours: "Volumen a la medida",
    files: "Lotes de archivos",
    note: "B2B | B2G",
    cta: { label: "Contactar con ventas", href: `mailto:${SITE.salesEmail}` },
  },
];

export const FAQ: { q: string; a: string }[] = [
  {
    q: "¿Qué puede evaluar Qualidot?",
    a: "Audio y video (llamadas, videollamadas, videoclases), además de imágenes y documentos PDF. Cada archivo se transcribe y se analiza con IA contra la rúbrica que definas.",
  },
  {
    q: "¿Qué es una rúbrica versionada?",
    a: "Una rúbrica es tu criterio de evaluación estructurado en criterios primarios y secundarios con pesos. Qualidot la versiona: cada cambio crea una versión nueva, así puedes comparar y auditar cómo evolucionó tu norma de calidad.",
  },
  {
    q: "¿Para quién es Qualidot?",
    a: "Para call centers y áreas de QA que evalúan interacciones, universidades y docentes que califican desempeño, y operación institucional (sector público, B2B/B2G) que necesita evaluación consistente y con evidencia.",
  },
  {
    q: "¿El scoring reemplaza el juicio humano?",
    a: "No: lo respalda. Qualidot entrega una calificación con la evidencia que la sustenta (fragmentos, presencia de criterios, guión), reduciendo la subjetividad y dándote trazabilidad para decidir.",
  },
  {
    q: "¿Cómo se cobra?",
    a: "Por planes mensuales según tu volumen de horas de audio/video y archivos procesados. Puedes empezar en el plan Básico y escalar; el plan Personalizado cubre volumen a la medida y contratos institucionales.",
  },
  {
    q: "¿Puedo usar rúbricas de otros usuarios?",
    a: "Sí. En el Rubriverso las rúbricas públicas se pueden adoptar completas o criterio por criterio: tu copia queda versionada como tuya y el linaje (de quién adoptaste qué) queda trazado. Las mejores suben en el ranking por calificaciones, visitas y adopciones reales.",
  },
  {
    q: "¿Qué evidencia entrega cada evaluación?",
    a: "La transcripción con marcas de tiempo, los fragmentos exactos donde se cumple (o incumple) cada criterio, la detección de guiones esperados y métricas de speech como muletillas por minuto. Cada calificación es auditable: puedes ir del score al momento preciso del audio o video.",
  },
  {
    q: "¿En qué idiomas funciona?",
    a: "El flujo está optimizado para español (transcripción, análisis y feedback en es-MX). El motor también procesa contenido en inglés; si tu operación es bilingüe, escríbenos para calibrar tu caso.",
  },
  {
    q: "¿Cómo cuidan mis datos y grabaciones?",
    a: "Tus archivos viven cifrados en tránsito y en reposo, con permisos granulares por usuario y bitácora de acceso. Tus rúbricas privadas y evaluaciones son solo tuyas: nada se comparte a la comunidad sin que tú lo publiques explícitamente.",
  },
  {
    q: "¿Necesito instalar algo o integrarme con mi conmutador/LMS?",
    a: "No para empezar: subes tus grabaciones (o las conectas desde tu nube — Google Drive, OneDrive, Dropbox) y evalúas. No dependes de integraciones nativas con Zoom, Meet o tu PBX para obtener valor desde el día uno.",
  },
  {
    q: "¿Qué pasa si mi rúbrica no funciona bien?",
    a: "Qualidot mide el performance de la rúbrica misma: consistencia entre evaluaciones, tendencia de mejora y criterios que nunca discriminan. Con esa traza puedes recalibrar umbrales y pesos — y cada ajuste queda como una versión nueva comparable con la anterior.",
  },
];

export const priceFmt = (mxn: number) =>
  new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 }).format(mxn);
